// Виджет «Закрытие» (ранее «Таймлайн») — полоска от создания карточки
// до закрытия/текущего момента относительно плановой погрузки.
// Общая логика для таблицы рейсов статистики (TripsTab) и 360-обзора
// (вкладки «Сводка»/«Распределение»).
import { fmtDate } from './format.js';

export const HOUR_MS = 60 * 60 * 1000;

export const TIMING_LABELS = {
  'tl-early': 'зелёный — закрыт не менее чем за 24 ч до погрузки',
  'tl-lastday': 'оранжевый — закрыт за 12–24 ч до погрузки',
  'tl-late': 'красный — закрыт менее чем за 12 ч до погрузки (или после)',
  'tl-neutral': 'серый — не закрыт / срок неизвестен',
};

export const TIMING_OPEN_LABELS = {
  'tl-early': 'зелёная штриховка — не закрыт, до погрузки более 24 ч',
  'tl-lastday': 'оранжевая штриховка — не закрыт, до погрузки 12–24 ч',
  'tl-late': 'красная штриховка — не закрыт, до погрузки менее 12 ч / просрочено',
  'tl-neutral': 'серая штриховка — не закрыт, срок неизвестен',
};

export function toTs(value) {
  if (value === null || value === undefined || value === '' || value !== value) return null;
  let normalized = value;
  if (typeof normalized === 'number' || /^\d+$/.test(normalized)) {
    const epoch = Number(normalized);
    normalized = epoch > 1e11 ? epoch : epoch * 1000;
  }
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
}

export function timingCls(closed, load, now = Date.now()) {
  if (load == null) return 'tl-neutral';
  // Закрытые: часы от закрытия карточки до погрузки.
  if (closed != null) {
    const hours = (load - closed) / HOUR_MS;
    if (hours >= 24) return 'tl-early';
    if (hours >= 12) return 'tl-lastday';
    return 'tl-late';
  }
  // Не закрытые: часы от текущего момента до погрузки (>24 ч — серый).
  const hoursLeft = (load - now) / HOUR_MS;
  if (hoursLeft > 24) return 'tl-neutral';
  if (hoursLeft >= 12) return 'tl-lastday';
  return 'tl-late';
}

// Модель виджета для строки рейса: { barPct, barCls, markPct, mark12Pct, tooltip }
// либо null, когда дат не хватает.
export function closeTimelineModel(row, { now = Date.now() } = {}) {
  if (!row) return null;
  const start = toTs(row.card_create_dt);
  if (start == null) return null;
  const closed = toTs(row.closed_at);
  const load = toTs(row.departure_planed_dt);
  const barEnd = closed ?? now;
  const end = load ?? barEnd;
  if (end < start) return null;
  const span = Math.max(end - start, HOUR_MS);
  const pct = (t) => Math.min(100, Math.max(0, ((t - start) / span) * 100));
  const cls = timingCls(closed, load, now);
  const hoursAtClose = closed != null && load != null
    ? ((load - closed) / HOUR_MS).toLocaleString('ru-RU', { maximumFractionDigits: 1 })
    : null;
  const hoursLeft = closed == null && load != null
    ? ((load - now) / HOUR_MS).toLocaleString('ru-RU', { maximumFractionDigits: 1 })
    : null;
  const tooltip = [
    `Создание: ${fmtDate(row.card_create_dt)}`,
    `Закрытие: ${closed != null ? fmtDate(row.closed_at) : '— (в работе)'}`,
    `Погрузка: ${load != null ? fmtDate(row.departure_planed_dt) : '—'}`,
    hoursAtClose != null ? `Часов до погрузки при закрытии: ${hoursAtClose}` : null,
    hoursLeft != null ? `Часов до погрузки (осталось): ${hoursLeft}` : null,
    closed != null ? TIMING_LABELS[cls] : TIMING_OPEN_LABELS[cls],
    '| — за 24 ч до погрузки · ¦ — за 12 ч · правый край — погрузка',
  ]
    .filter(Boolean)
    .join('\n');
  return {
    barPct: pct(barEnd),
    barCls: closed != null ? cls : `${cls} tl-open`,
    markPct: load != null ? pct(load - 24 * HOUR_MS) : null,
    mark12Pct: load != null ? pct(load - 12 * HOUR_MS) : null,
    tooltip,
  };
}
